import { ButtonHTMLAttributes, useCallback, useEffect, useMemo, useState } from 'react'

import { RemoteParticipant, RoomEvent, type Room } from 'livekit-client'
import { DataPacket_Kind, EgressInfo } from 'livekit-server-sdk'

import type { RecordButtonProps } from '@/src/components'
import { useRoomContext } from '@/src/context'

import { handlePublishData } from '@/src/helpers/handlePublishData'
import { mergeProps } from '@/src/mergeProps'

import { IMeetCodes } from '@/src/types/meetData.type'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import {
  initialRecordState,
  resetRecordState,
  selectRecordState,
  setRecordState,
  spinAndDisableRecordBtn,
} from '@/src/store/slices/record'
import { selectRoomState } from '@/src/store/slices/room'
import {
  selectBranding,
  selectIsAdmin,
  selectMeetCodes,
  selectSettings,
} from '@/src/store/slices/meetData'

const handleStartRecord = async (room: Room, livekit_url: string, meetCodes: IMeetCodes) => {
  const roomId = await room.getSid()
  const { company_id: companyCode, meet_id: meetCode } = meetCodes
  const urlParams = new URLSearchParams(window.location.search)
  const amoDeal = urlParams.get('amo_deal') || ''

  const params = new URLSearchParams({
    roomName: room.name,
    meetCode: meetCode?.toString(),
    companyCode: companyCode?.toString(),
    roomId,
    amoDeal,
  })
  const startEndpoint = `${window.origin}/api/start_template_record`
  const res = await fetch(`${startEndpoint}?livekit_url=${livekit_url}&${params.toString()}`)
  const { response } = await res.json()

  return response?.egressId
}

export const handleStopRecord = async (room: Room, livekit_url: string) => {
  console.log('END_RECORDING')

  try {
    const egressParams = new URLSearchParams({
      roomName: room.name,
      livekit_url,
    })

    const { response: egressList }: { response: EgressInfo[] } = await (
      await fetch(`${window.origin}/api/active_record_list?${egressParams.toString()}`)
    ).json()

    egressList.forEach(async (egress) => {
      const params = new URLSearchParams({ egressId: egress.egressId, roomName: room.name })
      const stopEndpoint = `${window.origin}/api/stop_record`

      await (await fetch(`${stopEndpoint}?livekit_url=${livekit_url}&${params.toString()}`)).json()
    })
  } catch (e) {
    console.error(e)
  }
}

function setupRecordButton() {
  const toggleRecord = async (room: Room, livekit_url: string, meetCodes: IMeetCodes) => {
    if (!room || !room?.name) {
      console.error('Room is undefined or has no name')
      return
    }

    room?.isRecording
      ? handleStopRecord(room, livekit_url)
      : handleStartRecord(room, livekit_url, meetCodes)
  }

  const className: string = 'lk-button'
  return { className, toggleRecord }
}

export function useRecordButton(props: RecordButtonProps) {
  const [displayButton, setDisplayButton] = useState<'none' | 'inline-flex'>('none')
  const room = useRoomContext()

  const { livekit_url } = useAppSelector(selectRoomState)
  const { recording_is_avalible, auto_rec } = useAppSelector(selectSettings)
  const isAdmin = useAppSelector(selectIsAdmin)
  const { disable, spinning, isRecordingUnavailable, isOpenUnavailableRecordingPopup } =
    useAppSelector(selectRecordState) ?? {}
  const { text_primary_color } = useAppSelector(selectBranding)
  const meetCodes = useAppSelector(selectMeetCodes)
  const dispatch = useAppDispatch()

  useEffect(() => {
    let timerId: NodeJS.Timeout = null

    // Если нам пришла ошибка с тем, чтобы запустить запись, то у нас будет вечно задизейблена кнопка.
    // Для этого вводим таймер на 30 сек, после которых наша кнопка разблокируется
    if (disable) {
      timerId = setTimeout(() => {
        dispatch(resetRecordState())
      }, 30000)
    }

    return () => {
      clearTimeout(timerId)
    }
  }, [disable, dispatch])

  useEffect(() => {
    const handleDisabledRecordButton = (isDataReceived: boolean = false) => {
      if (isDataReceived) {
        setTimeout(() => {
          dispatch(setRecordState(initialRecordState))
        }, 30000)
      } else {
        dispatch(setRecordState(initialRecordState))
      }
    }

    const handleDataReceived = (
      payload: Uint8Array,
      participant?: RemoteParticipant,
      kind?: DataPacket_Kind,
      topic?: string,
    ) => {
      // Если к нам пришла информация от другого пользователя с topic === 'recordButton'
      // это значит, что он либо включил, либо выключил запись.
      // Значит нужно заблокировать (задизейблить) кнопку для клика, чтобы случайно не нажать на нее повторно
      if (topic === 'recordButton') {
        dispatch(spinAndDisableRecordBtn())
        handleDisabledRecordButton(true)
      }
    }

    // recording status меняется ТОЛЬКО тогда, когда у нас либо подключился, либо уже отключился пользователь с флагом (полем) recorder: true
    // Значит мы можем либо еще раз начать запись, либо завершить ее и для этого включаем доступ к кнопке (раздизейбливаем)
    const handleRecordingStatusChanged = () => handleDisabledRecordButton(false)

    room
      .on(RoomEvent.DataReceived, handleDataReceived)
      .on(RoomEvent.RecordingStatusChanged, handleRecordingStatusChanged)

    return () => {
      room
        .off(RoomEvent.DataReceived, handleDataReceived)
        .off(RoomEvent.RecordingStatusChanged, handleRecordingStatusChanged)
    }
  }, [room, dispatch])

  useEffect(() => {
    const handleParticipantConnected = async () => {
      // Когда подключается новый пользователь в комнату и при этом у нас заблокирована (задизейблена) кнопка записи,
      // значит нам нужно также отключить ее ему, чтобы он ее случайно не нажал
      if (disable) {
        await handlePublishData(
          room,
          { disable: true, spinning: true },
          {
            reliable: true,
            topic: 'recordButton',
          },
        )
      }
    }

    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected)

    return () => {
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected)
    }
  }, [room, disable])

  useEffect(() => {
    // параметр, который отвечает за дисковое пространство.
    // Если оно будет переполнено, тогда запись будет недоступна
    if (recording_is_avalible === false) {
      dispatch(
        setRecordState({
          isRecording: false,
          disable: false,
          spinning: false,
          isOpenUnavailableRecordingPopup: isAdmin ?? false,
          isRecordingUnavailable: true,
        }),
      )
    }
  }, [dispatch, recording_is_avalible, isAdmin])

  useEffect(() => {
    const participantMetaData = JSON.parse(room.localParticipant.metadata || '{}')

    if (auto_rec || !participantMetaData?.isAdmin) {
      setDisplayButton('none')
    } else {
      setDisplayButton('inline-flex')
    }
  }, [room, auto_rec])

  const handleClickRecordButton = useCallback(async () => {
    if (isRecordingUnavailable) {
      if (!isOpenUnavailableRecordingPopup) {
        dispatch(
          setRecordState({
            isRecording: false,
            disable: false,
            spinning: false,
            isOpenUnavailableRecordingPopup: true,
            isRecordingUnavailable: true,
          }),
        )
      }
    } else {
      dispatch(spinAndDisableRecordBtn())

      // При любом нажатии на кнопку записи, мы говорим другим участникам,
      // что кнопка нажата и значит нужно ее заблокировать для клика (задизейблить)
      // до тех пор, пока egress не отключиться/подключиться
      await handlePublishData(
        room,
        { disable: true, spinning: true },
        {
          reliable: true,
          topic: 'recordButton',
        },
      )

      // Если мы нажали на кнопку, когда у нас была включена запись, значит мы хотим ее выключить.
      // Чтобы ее отключить, мы перманентно отключаем из комнаты пользователя с флагом (полем) recorder: true
      room.isRecording &&
        room?.remoteParticipants.forEach((participant) => {
          if (participant.permissions?.recorder === true) {
            handlePublishData(
              room,
              { disconnect: true },
              {
                destinationIdentities: [participant.identity],
                reliable: true,
                topic: 'disconnectParticipant',
              },
            )
          }
        })

      const { toggleRecord } = setupRecordButton()

      await toggleRecord(room, livekit_url, meetCodes)
    }
  }, [
    room,
    dispatch,
    isOpenUnavailableRecordingPopup,
    isRecordingUnavailable,
    livekit_url,
    meetCodes,
  ])

  const buttonProps: ButtonHTMLAttributes<HTMLButtonElement> = useMemo(() => {
    if (!room) {
      return {}
    }

    const { className } = setupRecordButton()
    let propsWithDisableField: RecordButtonProps = props

    if (disable) {
      propsWithDisableField = { ...props, disabled: true }
    }

    const mergedProps = mergeProps(propsWithDisableField, {
      className: `${props.className || ''} ${className}`,
      onClick: handleClickRecordButton,
      style: {
        ...props.style,
        position: 'relative',
        display: displayButton,
        opacity: isRecordingUnavailable || disable || spinning ? 0.5 : 1,
        color: text_primary_color,
      },
    })

    return mergedProps
  }, [
    displayButton,
    isRecordingUnavailable,
    disable,
    spinning,
    text_primary_color,
    props,
    room,
    handleClickRecordButton,
  ])

  return { buttonProps }
}
